/**
 * „Costul cu oamenii" din raportul financiar.
 *
 * Cifrele vin din fluturașii lunari (`PayrollEntry`), nu din extras, deci sunt
 * costul **angajat** al perioadei: brut + contribuții + CAM. Secțiunea pune
 * explicit alături cât a ieșit din bancă pe salarii, fiindcă diferența (de
 * regulă contribuțiile virate luna următoare) e exact lucrul care îl încurcă pe
 * antreprenor. Costul de aici NU se adaugă la cheltuielile perioadei.
 */
import { AlertTriangle } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { PeopleSection as PeopleData } from '../../../lib/monthlyReportApi';
import { fmtMoney, fmtPct, fmtSignedMoney } from '../../../lib/monthlyReportFormat';
import { bucketLabel, type ReportPeriodKind } from '../../../lib/reportPeriod';
import { CHART_TOOLTIP_LABEL_STYLE, CHART_TOOLTIP_STYLE } from '../../../lib/chartTheme';
import { useI18n } from '../../../contexts/I18nContext';
import { ChangeHint, Empty, PartyList, ReportSection, Stat } from './ReportSections';

const AXIS = { fill: '#6B7280', fontSize: 11 };
const GRID = '#E5E7EB';
const PEOPLE_COLOR = '#0891b2';

function compact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

export function PeopleSection({
  people,
  bucketKind = 'month',
}: {
  people: PeopleData;
  bucketKind?: ReportPeriodKind;
}) {
  const { t, locale } = useI18n();
  const hasPayroll = people.totalCost > 0;
  const hoursLabel = (n: number) => n.toLocaleString(locale === 'ro' ? 'ro-RO' : 'en-GB');
  const trend = people.trend.map((p) => ({
    label: bucketLabel(p.period, bucketKind, locale),
    cost: p.totalCost,
  }));

  return (
    <ReportSection
      title={t('monthlyReport.section.people')}
      aside={
        hasPayroll ? (
          <span className="text-xs text-gray-500">
            {t('monthlyReport.people.headcountValue', { n: people.headcount })}
          </span>
        ) : undefined
      }
    >
      {!hasPayroll && people.hoursTotal === 0 ? (
        <Empty text={t('monthlyReport.people.empty')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
            <Stat
              label={t('monthlyReport.people.totalCost')}
              value={fmtMoney(people.totalCost, 'RON', locale)}
              hint={
                <ChangeHint
                  change={pctChange(people.totalCost, people.previousTotalCost)}
                  fallback={t('monthlyReport.summary.noPrevious')}
                />
              }
            />
            <Stat
              label={t('monthlyReport.people.shareOfOutflows')}
              value={people.shareOfOutflows === null ? '—' : fmtPct(people.shareOfOutflows)}
              hint={
                people.shareOfInflows === null
                  ? undefined
                  : `${t('monthlyReport.people.shareOfInflows')}: ${fmtPct(people.shareOfInflows)}`
              }
            />
            <Stat
              label={t('monthlyReport.people.revenuePerPerson')}
              value={
                people.revenuePerPerson === null
                  ? '—'
                  : fmtMoney(people.revenuePerPerson, 'RON', locale)
              }
            />
            <Stat
              label={t('monthlyReport.people.returnPerLeu')}
              value={people.returnPerLeu === null ? '—' : people.returnPerLeu.toFixed(2)}
              tone={
                people.returnPerLeu === null
                  ? 'neutral'
                  : people.returnPerLeu >= 2
                    ? 'good'
                    : people.returnPerLeu < 1
                      ? 'bad'
                      : 'neutral'
              }
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium text-gray-700">
                {t('monthlyReport.people.breakdown')}
              </h3>
              <dl className="mt-2 space-y-1 text-sm">
                {(
                  [
                    ['monthlyReport.people.gross', people.breakdown.gross],
                    ['monthlyReport.people.contributions', people.breakdown.contributions],
                    ['monthlyReport.people.incomeTax', people.breakdown.incomeTax],
                    ['monthlyReport.people.cam', people.breakdown.cam],
                    ['monthlyReport.people.net', people.breakdown.net],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <dt className="text-gray-600">{t(key)}</dt>
                    <dd className="tabular-nums text-gray-900">{fmtMoney(value, 'RON', locale)}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <PartyList title={t('monthlyReport.people.byDepartment')} rows={people.byDepartment} />
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <h3 className="text-sm font-medium text-gray-700">
              {t('monthlyReport.people.reconciliation')}
            </h3>
            <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('monthlyReport.people.ledgerPaid')}</dt>
                <dd className="tabular-nums text-gray-900">
                  {fmtMoney(people.ledgerPaid, 'RON', locale)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('monthlyReport.people.totalCost')}</dt>
                <dd className="tabular-nums text-gray-900">
                  {fmtMoney(people.totalCost, 'RON', locale)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('monthlyReport.people.ledgerVsPayroll')}</dt>
                <dd className="tabular-nums font-medium text-gray-900">
                  {fmtSignedMoney(people.ledgerVsPayroll, 'RON', locale)}
                </dd>
              </div>
              {people.ledgerDividends > 0 && (
                <div className="flex justify-between">
                  <dt className="text-gray-600">{t('monthlyReport.people.ledgerDividends')}</dt>
                  <dd className="tabular-nums text-gray-900">
                    {fmtMoney(people.ledgerDividends, 'RON', locale)}
                  </dd>
                </div>
              )}
            </dl>
            <p className="mt-2 text-xs text-gray-500">
              {t('monthlyReport.people.reconciliationHint')}
            </p>
          </div>

          {people.rows.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="pb-2 pr-3 font-medium">{t('monthlyReport.people.person')}</th>
                    <th className="pb-2 pr-3 font-medium">
                      {t('monthlyReport.people.department')}
                    </th>
                    <th className="pb-2 pr-3 text-right font-medium">
                      {t('monthlyReport.people.totalCost')}
                    </th>
                    <th className="pb-2 pr-3 text-right font-medium">
                      {t('monthlyReport.people.gross')}
                    </th>
                    <th className="pb-2 pr-3 text-right font-medium">
                      {t('monthlyReport.people.hours')}
                    </th>
                    <th className="pb-2 pr-3 text-right font-medium">
                      {t('monthlyReport.people.costPerHour')}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t('monthlyReport.people.leaveDays')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {people.rows.map((p) => (
                    <tr key={p.personId}>
                      <td className="py-1.5 pr-3 text-gray-800">{p.name}</td>
                      <td className="py-1.5 pr-3 text-gray-500">{p.departmentName ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">
                        {fmtMoney(p.totalCost, 'RON', locale)}
                        <span className="ml-2 text-xs text-gray-400">{fmtPct(p.share)}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                        {fmtMoney(p.grossSalary, 'RON', locale)}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                        {p.hours > 0 ? hoursLabel(p.hours) : '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">
                        {p.costPerHour === null ? '—' : fmtMoney(p.costPerHour, 'RON', locale)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-gray-600">
                        {p.leaveDays > 0 ? p.leaveDays : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 text-xs text-gray-500">
            {t('monthlyReport.people.hoursTotal')}: {hoursLabel(people.hoursTotal)}
            {people.hoursUnbooked > 0 && (
              <>
                {' · '}
                {t('monthlyReport.people.hoursUnbooked')}: {hoursLabel(people.hoursUnbooked)}
              </>
            )}
          </p>

          {trend.some((p) => p.cost > 0) && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700">
                {t('monthlyReport.people.trend')}
              </h3>
              <div className="mt-2 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={AXIS}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={12}
                    />
                    <YAxis
                      tick={AXIS}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={compact}
                      width={44}
                    />
                    <Tooltip
                      contentStyle={CHART_TOOLTIP_STYLE}
                      labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                      formatter={(v) => fmtMoney(v as number, 'RON', locale)}
                      cursor={{ fill: '#F3F4F6' }}
                    />
                    <Bar
                      dataKey="cost"
                      name={t('monthlyReport.people.totalCost')}
                      fill={PEOPLE_COLOR}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {people.warnings.map((w) => (
            <p key={w} className="mt-2 flex items-start gap-1.5 text-xs text-gray-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span>
                {t(
                  `monthlyReport.people.warning.${w}` as 'monthlyReport.people.warning.payroll_missing',
                )}
              </span>
            </p>
          ))}
        </>
      )}
    </ReportSection>
  );
}
